import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link, useParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import PasswordInput from './shared/PasswordInput';
import { Label } from '../components/ui/label';
import { LOGIN } from '../constants/testIds';
import { toast } from '../hooks/use-toast';
import { useAuth } from './AuthContext';
import { useLang } from './LanguageContext';
import { api, clearImpersonation } from './api';
import MfaStep from './MfaStep';
import { consumePostLoginNext } from './postLoginNext';
import { homeForRole } from './externalRoles';
import { brandAssetUrl } from '../branding';
import { Mail, Lock, User as UserIcon, ArrowRight, Home, Sparkles } from 'lucide-react';

const INVITE_ONLY_MSG = 'Sign-ups are invite-only right now. Ask your team admin for an invitation.';

// Translated login copy — mirrors the tour i18n so referred visitors land in their language.
// Anonymous per-browser id sent with referral hits and the referral signup so the
// server can match the two (ticket 207). Stored once; a fresh one if storage is blocked.
const referralVisitorId = () => {
  const make = () => `v_${(window.crypto?.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, '').slice(0, 32)}`;
  try {
    let id = localStorage.getItem('bfy_ref_visitor');
    if (!id) { id = make(); localStorage.setItem('bfy_ref_visitor', id); }
    return id;
  } catch { return make(); }
};

const LOGIN_I18N = {
  en: { welcome: 'Welcome back', signInSub: 'Sign in to access your BrokerForYou dashboard.',
        create: 'Create your account', createSub: 'Get started with the brokerage OS.',
        fullName: 'Full Name', email: 'Email', password: 'Password', showPassword: 'Show password', hidePassword: 'Hide password', brokerage: 'Brokerage Name',
        brokerageHelp: "We'll spin up your BrokerForYou workspace with this name.",
        signIn: 'Sign in', createAccount: 'Create account',
        already: 'Already have an account?', dontHave: "Don't have an account?",
        signInLink: 'Sign in', signUpLink: 'Sign up',
        continueGoogle: 'Continue with Google', or: 'OR', backHome: 'Back to home',
        refBanner: '🎁 You were referred by a broker — sign up and you both get a month free.' },
  es: { welcome: 'Bienvenido', signInSub: 'Inicia sesión para acceder a tu panel de BrokerForYou.',
        create: 'Crea tu cuenta', createSub: 'Empieza con el sistema operativo para corredurías.',
        fullName: 'Nombre completo', email: 'Correo', password: 'Contraseña', showPassword: 'Mostrar contraseña', hidePassword: 'Ocultar contraseña', brokerage: 'Nombre de la Correduría',
        brokerageHelp: 'Crearemos tu espacio en BrokerForYou con este nombre.',
        signIn: 'Entrar', createAccount: 'Crear cuenta',
        already: '¿Ya tienes cuenta?', dontHave: '¿No tienes cuenta?',
        signInLink: 'Entra', signUpLink: 'Regístrate',
        continueGoogle: 'Continuar con Google', or: 'O', backHome: 'Volver al inicio',
        refBanner: '🎁 Te recomendó otro broker — al registrarte, ambos reciben un mes gratis.' },
  pt: { welcome: 'Bem-vindo', signInSub: 'Entre para acessar seu painel BrokerForYou.',
        create: 'Crie sua conta', createSub: 'Comece com o sistema operativo para corretoras.',
        fullName: 'Nome completo', email: 'E-mail', password: 'Senha', showPassword: 'Mostrar senha', hidePassword: 'Ocultar senha', brokerage: 'Nome da Corretora',
        brokerageHelp: 'Vamos criar seu espaço no BrokerForYou com este nome.',
        signIn: 'Entrar', createAccount: 'Criar conta',
        already: 'Já tem uma conta?', dontHave: 'Não tem conta?',
        signInLink: 'Entrar', signUpLink: 'Cadastre-se',
        continueGoogle: 'Continuar com Google', or: 'OU', backHome: 'Voltar ao início',
        refBanner: '🎁 Um broker te indicou — ao se cadastrar, os dois ganham um mês grátis.' },
  zh: { welcome: '欢迎回来', signInSub: '登录以访问您的 BrokerForYou 仪表板。',
        create: '创建您的账户', createSub: '开始使用经纪业务操作系统。',
        fullName: '姓名', email: '邮箱', password: '密码', showPassword: '显示密码', hidePassword: '隐藏密码', brokerage: '经纪公司名称',
        brokerageHelp: '我们将以此名称为您创建 BrokerForYou 工作区。',
        signIn: '登录', createAccount: '创建账户',
        already: '已有账户？', dontHave: '还没有账户？',
        signInLink: '登录', signUpLink: '注册',
        continueGoogle: '使用 Google 继续', or: '或', backHome: '返回首页',
        refBanner: '🎁 一位经纪人推荐了您 — 注册后，你们双方都可获得一个月免费。' },
  fr: { welcome: 'Bon retour', signInSub: 'Connectez-vous pour accéder à votre tableau BrokerForYou.',
        create: 'Créez votre compte', createSub: "Démarrez avec le système d'exploitation pour courtiers.",
        fullName: 'Nom complet', email: 'E-mail', password: 'Mot de passe', showPassword: 'Afficher le mot de passe', hidePassword: 'Masquer le mot de passe', brokerage: 'Nom du courtage',
        brokerageHelp: 'Nous créerons votre espace BrokerForYou avec ce nom.',
        signIn: 'Se connecter', createAccount: 'Créer un compte',
        already: 'Vous avez déjà un compte ?', dontHave: "Vous n'avez pas de compte ?",
        signInLink: 'Connexion', signUpLink: "S'inscrire",
        continueGoogle: 'Continuer avec Google', or: 'OU', backHome: "Retour à l'accueil",
        refBanner: "🎁 Un courtier vous a recommandé — inscrivez-vous et vous obtenez tous les deux un mois gratuit." },
  de: { welcome: 'Willkommen zurück', signInSub: 'Melden Sie sich für Ihr BrokerForYou-Dashboard an.',
        create: 'Konto erstellen', createSub: 'Starten Sie mit dem Betriebssystem für Makler.',
        fullName: 'Vollständiger Name', email: 'E-Mail', password: 'Passwort', showPassword: 'Passwort anzeigen', hidePassword: 'Passwort verbergen', brokerage: 'Firmenname',
        brokerageHelp: 'Wir richten Ihren BrokerForYou-Arbeitsbereich mit diesem Namen ein.',
        signIn: 'Anmelden', createAccount: 'Konto erstellen',
        already: 'Sie haben bereits ein Konto?', dontHave: 'Noch kein Konto?',
        signInLink: 'Anmelden', signUpLink: 'Registrieren',
        continueGoogle: 'Mit Google fortfahren', or: 'ODER', backHome: 'Zurück zur Startseite',
        refBanner: '🎁 Ein Makler hat Sie empfohlen — registrieren Sie sich und Sie beide erhalten einen Monat gratis.' },
  it: { welcome: 'Bentornato', signInSub: 'Accedi alla tua dashboard BrokerForYou.',
        create: 'Crea il tuo account', createSub: "Inizia con l'OS per agenzie di brokerage.",
        fullName: 'Nome completo', email: 'Email', password: 'Password', showPassword: 'Mostra password', hidePassword: 'Nascondi password', brokerage: "Nome dell'agenzia",
        brokerageHelp: 'Creeremo il tuo spazio BrokerForYou con questo nome.',
        signIn: 'Accedi', createAccount: 'Crea account',
        already: 'Hai già un account?', dontHave: 'Non hai un account?',
        signInLink: 'Accedi', signUpLink: 'Registrati',
        continueGoogle: 'Continua con Google', or: 'O', backHome: 'Torna alla home',
        refBanner: '🎁 Ti ha invitato un broker — registrati e ricevete entrambi un mese gratis.' },
  vi: { welcome: 'Chào mừng trở lại', signInSub: 'Đăng nhập để truy cập bảng điều khiển BrokerForYou.',
        create: 'Tạo tài khoản', createSub: 'Bắt đầu với hệ điều hành cho các công ty môi giới.',
        fullName: 'Họ và tên', email: 'Email', password: 'Mật khẩu', showPassword: 'Hiện mật khẩu', hidePassword: 'Ẩn mật khẩu', brokerage: 'Tên công ty môi giới',
        brokerageHelp: 'Chúng tôi sẽ tạo không gian BrokerForYou với tên này.',
        signIn: 'Đăng nhập', createAccount: 'Tạo tài khoản',
        already: 'Đã có tài khoản?', dontHave: 'Chưa có tài khoản?',
        signInLink: 'Đăng nhập', signUpLink: 'Đăng ký',
        continueGoogle: 'Tiếp tục với Google', or: 'HOẶC', backHome: 'Về trang chủ',
        refBanner: '🎁 Một môi giới đã giới thiệu bạn — đăng ký và cả hai đều nhận được một tháng miễn phí.' },
};

const LANG_LIST = [
  { k: 'en', flag: '🇺🇸', label: 'EN' },
  { k: 'es', flag: '🇪🇸', label: 'ES' },
  { k: 'pt', flag: '🇧🇷', label: 'PT' },
  { k: 'zh', flag: '🇨🇳', label: 'ZH' },
  { k: 'fr', flag: '🇫🇷', label: 'FR' },
  { k: 'de', flag: '🇩🇪', label: 'DE' },
  { k: 'it', flag: '🇮🇹', label: 'IT' },
  { k: 'vi', flag: '🇻🇳', label: 'VI' },
];

export default function LoginPage() {
  const [mode, setMode] = useState(() => {
    // Referred visitors intend to sign up — default to signup mode straight away
    try { return (new URLSearchParams(window.location.search).get('ref')) || window.location.pathname.endsWith('/signup') ? 'signup' : 'login'; } catch { return 'login'; }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [tenantSkin, setTenantSkin] = useState(null);   // whitelabel branding fetched by slug
  const [mfa, setMfa] = useState(null); // {mfaToken, enrolled} while a login is pending its second factor
  // Ticket 166: {email} once a signup has emailed its verification
  // link — the form gives way to a "check your email" panel with Resend.
  const [sent, setSent] = useState(null);
  const [resending, setResending] = useState(false);
  // Ticket 117: PUBLIC_SIGNUP_ENABLED=0 on the API makes the app invite-only.
  // null until /auth/config answers; the backend 403s regardless, this only
  // keeps the UI honest (no "Sign up" link that can't work).
  const [publicSignup, setPublicSignup] = useState(null);
  const { login, completeMfa, signup, user, loading } = useAuth();
  const { lang, setLang } = useLang();
  const L = LOGIN_I18N[lang] || LOGIN_I18N.en;
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { slug } = useParams();
  const from = location.state?.from || '/portal';

  useEffect(() => {
    api.get('/auth/config')
      .then((r) => setPublicSignup(r.data?.public_signup !== false))
      .catch(() => setPublicSignup(true));
  }, []);
  useEffect(() => {
    if (publicSignup === false && mode === 'signup') {
      setMode('login');
      toast({ title: 'Invite only', description: INVITE_ONLY_MSG });
    }
  }, [publicSignup, mode]);

  // Fetch tenant branding when the route is /portal/login/:slug so the login screen
  // wears the brokerage's own logo + accent color. Falls through to BrokerForYou default.
  useEffect(() => {
    if (!slug) { setTenantSkin(null); return; }
    api.get(`/tenants/public/${encodeURIComponent(slug)}`)
      .then((r) => setTenantSkin(r.data))
      .catch(() => setTenantSkin(null));
  }, [slug]);

  // Track ?ref=CODE hits so referrals are attributed later at signup time. The
  // visitor id gives each visitor their own pending row, which signup then claims.
  useEffect(() => {
    const ref = params.get('ref');
    if (ref) {
      try { localStorage.setItem('bfy_ref_code', ref); } catch {}
      api.post('/referrals/track', { code: ref, visitor_id: referralVisitorId() }).catch(() => {});
    }
  }, [params]);

  // Landing on the login page ends any login-as state (ticket 73): an expired
  // impersonation token must not leave a banner for whoever signs in next.
  useEffect(() => { clearImpersonation(); }, []);

  // Redirect to invite acceptance if invite code present
  useEffect(() => {
    const invite = params.get('invite');
    if (invite) navigate(`/portal/invite?invite=${invite}`, { replace: true });
  }, [params, navigate]);

  // One redirect per sign-in. Signing in sets `user`, so the effect below and
  // completeLogin both fire; whichever runs first decides, the other is a no-op
  // (otherwise the loser overwrites a remembered deal link with the portal home).
  const redirected = useRef(false);
  const goAfterLogin = (u) => {
    if (redirected.current) return;
    redirected.current = true;
    // A deal link's remembered destination wins, but only if it is a /deal/<id> path.
    const next = consumePostLoginNext();
    const dest = next || homeForRole(u.role, from);
    navigate(dest, { replace: true });
  };

  // Already signed in (verified via /auth/me) — go straight to the portal
  useEffect(() => {
    if (!loading && user) goAfterLogin(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, navigate, from]);

  // AuthCallback redirects here (instead of completing the login itself) when
  // the Google sign-in it just exchanged still needs a second factor. Clear
  // the router state once consumed so a later back-navigation to this page
  // doesn't re-arm MfaStep with an already-spent challenge token.
  useEffect(() => {
    if (location.state?.mfa) {
      setMfa(location.state.mfa);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    } else if (location.state?.error) {
      // AuthCallback hands back the API's own message (e.g. invite-only 403).
      toast({ title: 'Sign-in failed', description: location.state.error });
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [brokerageName, setBrokerageName] = useState('');
  const refCode = (() => {
    const urlRef = params.get('ref');
    if (urlRef) return urlRef;
    try { return localStorage.getItem('bfy_ref_code'); } catch { return null; }
  })();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let u;
      if (mode === 'login') {
        u = await login(email, password);
      } else if (refCode) {
        // Referral signup (ticket 166): the brokerage workspace is created
        // when the emailed link is clicked, not here.
        const { data } = await api.post('/auth/signup-with-referral', {
          email, name,
          brokerage_name: brokerageName || `${name}'s Brokerage`,
          ref: refCode,
          visitor_id: referralVisitorId(),
        });
        try { localStorage.removeItem('bfy_ref_code'); } catch {}
        u = { verificationSent: true, email: data.email };
      } else {
        u = await signup({ email, name });
      }
      if (u?.mfaRequired) {
        setMfa({ mfaToken: u.mfaToken, enrolled: u.enrolled });
        return;
      }
      if (u?.verificationSent) {
        // Ticket 166 (and 115's borrower-seat claim): nothing to log in with
        // until the emailed link is clicked; the password is set there.
        setSent({ email: u.email });
        return;
      }
      completeLogin(u);
    } catch (e) {
      toast({ title: 'Login failed', description: e.response?.data?.detail || 'Please check your credentials.' });
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      // Always 200 by design (no enumeration); the per-address cap applies.
      await api.post('/auth/signup/resend', { email: sent.email });
      toast({ title: 'Email sent', description: `If ${sent.email} is waiting on a link, a fresh one is on its way.` });
    } catch (e) {
      toast({ title: 'Could not resend', description: e.response?.data?.detail || 'Try again in a few minutes.' });
    } finally {
      setResending(false);
    }
  };

  // Shared post-login redirect — used both for a normal login and after the
  // MFA step hands back the finalized session.
  const completeLogin = (u) => {
    toast({ title: 'Welcome!', description: 'Redirecting...' });
    goAfterLogin(u);
  };

  const handleMfaDone = (data) => {
    const u = completeMfa(data);
    completeLogin(u);
  };

  const googleLogin = () => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      toast({ variant: 'destructive', title: 'Google sign-in unavailable', description: 'Not configured for this environment yet.' });
      return;
    }
    const redirectUri = window.location.origin + '/portal/auth/callback';
    const state = crypto.randomUUID();
    sessionStorage.setItem('google_oauth_state', state);
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      state,
      prompt: 'select_account',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  // Whitelabel palette — use tenant colors when a slug is loaded, else BrokerForYou defaults.
  const skin = tenantSkin?.colors || {};
  const accent = skin.accent || '#8DC63F';
  const primary = skin.primary || '#070B5F';
  const coverPhoto = brandAssetUrl(tenantSkin?.cover_photo_url);

  // If tenant uploaded a cover photo, use it as the background + darken it so text is legible.
  // Otherwise fall through to the branded gradient.
  const leftStyle = coverPhoto
    ? {
        backgroundImage: `linear-gradient(160deg, ${primary}dd 0%, ${primary}aa 50%, ${primary}ee 100%), url(${coverPhoto})`,
        backgroundSize: 'cover, cover',
        backgroundPosition: 'center, center',
      }
    : {
        background: tenantSkin
          ? `linear-gradient(160deg, ${primary}dd 0%, ${primary} 60%, ${primary}ee 100%)`
          : 'linear-gradient(160deg, #030524 0%, #070B5F 60%, #0F1780 100%)',
      };
  const brandLogo = brandAssetUrl(tenantSkin?.logo_url) || '/brand/brokerforyou-white.png?v=3';
  const brandName = tenantSkin?.name || 'BrokerForYou';
  const brandTagline = tenantSkin?.content?.hero_subtitle
    || 'The all-in-one operating system for mortgage brokers and real estate teams. CRM, calculators, marketing and Audrey AI — under one roof.';

  return (
    <div className="min-h-screen flex" style={{ background: '#faf7f2' }} data-testid={tenantSkin ? 'whitelabel-login' : 'default-login'}>
      {/* Left — branding */}
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden" style={leftStyle} data-testid="login-left-panel">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `radial-gradient(circle at 30% 20%, ${accent}55 0%, transparent 45%), radial-gradient(circle at 70% 80%, ${primary}88 0%, transparent 45%)` }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition">
            <Home size={14} /> {L.backHome}
          </Link>
          <div>
            <div className="inline-block">
              <img src={brandLogo} alt={brandName} className="h-20 w-auto max-w-[300px] object-contain" data-testid="login-logo" />
            </div>
            <p className="mt-8 text-white/70 text-lg max-w-md leading-relaxed" data-testid="login-tagline">
              {brandTagline}
            </p>
            <div className="mt-8 flex items-center gap-2 text-sm text-white/60">
              <Sparkles size={14} style={{ color: accent }} />
              {tenantSkin ? `Powered by BrokerForYou` : 'Powered by Claude'}
            </div>
          </div>
          <div className="text-xs text-white/40">© {new Date().getFullYear()} {brandName}. All rights reserved.</div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {mfa ? (
            <MfaStep
              mfaToken={mfa.mfaToken}
              enrolled={mfa.enrolled}
              onDone={handleMfaDone}
              onCancel={() => setMfa(null)}
            />
          ) : sent ? (
            <div data-testid="signup-check-email">
              <Mail size={28} style={{ color: '#6BA82E' }} />
              <h2 className="font-serif text-3xl font-semibold mt-4" style={{ color: '#070B5F' }}>Check your email</h2>
              {/* Always "sent": the API never says whether delivery worked (ticket 166). */}
              <p className="text-slate-500 mt-2">
                We sent a link to <b>{sent.email}</b>. Click it to choose your password and finish
                setting up your account. The link expires in 24 hours.
              </p>
              <button type="button" onClick={resend} disabled={resending} className="btn-gold w-full justify-center mt-6" data-testid="signup-resend">
                {resending ? 'Please wait...' : 'Resend email'}
              </button>
              <button type="button" onClick={() => setSent(null)} className="text-sm font-semibold hover:underline mt-4 w-full text-center" style={{ color: '#6BA82E' }} data-testid="signup-use-different-email">
                Use a different email
              </button>
            </div>
          ) : (
          <>
          {/* Language chip strip — always visible so referred visitors and returning users land in the right language */}
          <div className="flex justify-end mb-4 gap-1" data-testid="login-lang-picker">
            {LANG_LIST.map((l) => (
              <button
                key={l.k}
                onClick={() => setLang(l.k)}
                data-testid={`login-lang-${l.k}`}
                className="text-[11px] font-semibold px-2 py-1 rounded-md transition"
                style={lang === l.k
                  ? { background: '#8DC63F20', color: '#4d7a1e' }
                  : { color: '#94a3b8' }}
              >{l.flag} {l.label}</button>
            ))}
          </div>

          <div className="lg:hidden mb-8 text-center">
            <img src="/brand/brokerforyou-dark.png?v=3" alt="BrokerForYou" className="h-16 w-auto mx-auto" />
          </div>
          <h2 className="font-serif text-3xl font-semibold" style={{ color: '#070B5F' }} data-testid="login-title">
            {mode === 'login' ? L.welcome : L.create}
          </h2>
          <p className="text-slate-500 mt-2">
            {mode === 'login' ? L.signInSub : L.createSub}
          </p>

          <button onClick={googleLogin} className="mt-8 w-full flex items-center justify-center gap-3 bg-white border border-slate-200 rounded-xl py-3 px-4 font-semibold text-sm text-slate-700 hover:border-slate-400 transition-colors shadow-sm">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 15.6 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.2 5.2C41.4 35.4 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            {L.continueGoogle}
          </button>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 uppercase tracking-widest">{L.or}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === 'signup' && refCode && (
              <div className="rounded-lg p-3 text-[12px] font-semibold" style={{ background: '#8DC63F15', color: '#4d7a1e', border: '1px solid #8DC63F40' }} data-testid="referral-banner">
                {L.refBanner}
              </div>
            )}
            {mode === 'signup' && (
              <div>
                <Label className="text-sm" style={{ color: '#070B5F' }}>{L.fullName}</Label>
                <div className="relative mt-1.5">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input value={name} onChange={(e) => setName(e.target.value)} required className="pl-10" placeholder="Jane Officer" />
                </div>
              </div>
            )}
            {mode === 'signup' && refCode && (
              <div>
                <Label className="text-sm" style={{ color: '#070B5F' }}>{L.brokerage}</Label>
                <div className="relative mt-1.5">
                  <Input value={brokerageName} onChange={(e) => setBrokerageName(e.target.value)} required className="pl-3" placeholder="Sunbelt Realty" data-testid="brokerage-name" />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{L.brokerageHelp}</p>
              </div>
            )}
            <div>
              <Label htmlFor="login-email" className="text-sm" style={{ color: '#070B5F' }}>{L.email}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10" placeholder="you@company.com" data-testid={LOGIN.emailInput} />
              </div>
            </div>
            {/* Ticket 166: signup has no password field — it is chosen at the emailed link. */}
            {mode === 'login' && (
              <div>
                <Label htmlFor="login-password" className="text-sm" style={{ color: '#070B5F' }}>{L.password}</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <PasswordInput id="login-password" showLabel={L.showPassword} hideLabel={L.hidePassword} value={password} onChange={(e) => setPassword(e.target.value)} required className="pl-10" placeholder="••••••••" data-testid={LOGIN.passwordInput} />
                </div>
                <div className="text-right mt-1.5">
                  <Link to="/portal/forgot-password" className="text-sm font-medium hover:underline" style={{ color: '#6BA82E' }} data-testid={LOGIN.forgotPasswordLink}>
                    Forgot password?
                  </Link>
                </div>
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-gold w-full justify-center mt-2" data-testid="login-submit">
              {busy ? 'Please wait...' : <>{mode === 'login' ? L.signIn : L.createAccount} <ArrowRight size={16} /></>}
            </button>
          </form>

          {publicSignup === false ? (
            <p className="text-sm text-slate-500 mt-6 text-center" data-testid="invite-only-note">{INVITE_ONLY_MSG}</p>
          ) : (
          <p className="text-sm text-slate-500 mt-6 text-center">
            {mode === 'login' ? L.dontHave : L.already}{' '}
            <button className="font-semibold hover:underline" style={{ color: '#6BA82E' }} onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} data-testid="toggle-mode">
              {mode === 'login' ? L.signUpLink : L.signInLink}
            </button>
          </p>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
